import { Box, Flex, Image, Input, Text, VStack } from '@chakra-ui/react';
import heic2any from 'heic2any';
import { useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { captureListState } from '../lib/atoms/orderState';

interface ICameraButton {
  slotId: number;
  onPreviewImage: (path: string) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

function CaptureButton(props: ICameraButton) {
  const [captureList, setCaptureList] = useRecoilState(captureListState);
  const [imagePreview, setImagePreview] = useState<string>('');

  const checkExtentionNames = (file: File) => {
    return file.name.includes('.jpeg') ||
      file.name.includes('.png') ||
      file.name.includes('.heic') ||
      file.name.includes('.heif')
      ? true
      : false;
  };

  async function convertHEICtoJPEG(file: File) {
    try {
      props.setLoading(true);
      const jpegBlob: any = await heic2any({
        blob: file,
        toType: 'image/jpeg',
      });
      const jpegFile = new File([jpegBlob], `converted_${file.name}.jpg`, {
        type: 'image/jpeg',
      });
      return jpegFile;
    } catch (error) {
      alert('사진 변환(HEIC)에 실패하였습니다. JPEG/PNG 포맷을 권장합니다');
      return null;
    } finally {
      props.setLoading(false);
    }
  }

  const convertImage = async (file: File) => {
    if (
      file.type.includes('heic') ||
      file.type.includes('heif') ||
      file.name.includes('.heic') ||
      file.name.includes('.heif')
    ) {
      const convertFile = await convertHEICtoJPEG(file);
      if (convertFile === null) {
        return file;
      }
      return convertFile;
    }
    return file;
  };

  const handleFileInput = useRef<HTMLInputElement>(null);
  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // console.log(event.target.files);
    if (event.target.files) {
      if (event.target.files[0].type.includes('image')) {
        if (
          event.target.files[0].type.includes('jpeg') ||
          event.target.files[0].type.includes('png') ||
          event.target.files[0].type.includes('heic') ||
          event.target.files[0].type.includes('heif')
        ) {
          const found = captureList.find(
            (capture) => capture.slotId === props.slotId
          );
          if (found === undefined) {
            const convertFile = await convertImage(event.target.files[0]);
            const previewImage = URL.createObjectURL(convertFile);
            setCaptureList([
              ...captureList,
              {
                slotId: props.slotId,
                imagePreview: previewImage,
                imageFile: convertFile,
              },
            ]);
            setImagePreview(previewImage);
          }
        } else {
          alert('지원하지 않는 포맷입니다. JPEG/PNG/HEIC/HEIF만 가능합니다.');
        }
      } else if (checkExtentionNames(event.target.files[0])) {
        const found = captureList.find(
          (capture) => capture.slotId === props.slotId
        );
        if (found === undefined) {
          const convertFile = await convertImage(event.target.files[0]);
          const previewImage = URL.createObjectURL(convertFile);
          setCaptureList([
            ...captureList,
            {
              slotId: props.slotId,
              imagePreview: previewImage,
              imageFile: convertFile,
            },
          ]);
          setImagePreview(previewImage);
        }
      } else {
        alert('지원하지 않는 포맷입니다. 사진 업로드만 가능합니다.');
      }
    }
  };

  const onDeleteImage = () => {
    const target = captureList.findIndex(
      (capture) => capture.slotId === props.slotId
    );
    if (target < 0) return;

    const updatedList = [...captureList];
    updatedList.splice(target, 1);
    setCaptureList(updatedList);
    setImagePreview('');
  };

  return (
    <Box
      boxSize={'5rem'}
      position={'relative'}
      border="1px"
      borderColor={'#eeeeee'}
      borderRadius="0.25rem"
      borderStyle={'dotted'}
      onClick={() => {
        handleFileInput.current?.click();
      }}>
      {imagePreview === '' ? (
        <>
          <Box as="label">
            <Input
              style={{ display: 'none' }}
              type="file"
              // accept="image/*"
              // capture="environment"
              ref={handleFileInput}
              onChange={handleImageChange}
            />
          </Box>
          <VStack
            position={'absolute'}
            top="0"
            left="0"
            right="0"
            bottom="0"
            justifyContent={'center'}
            spacing={'0.375rem'}>
            <Image src="/assets/images/icn_upload.svg" />
            <Text fontSize={'0.75rem'} fontWeight="medium" color={'#757575'}>
              {props.slotId === 0 ? '전체' : '상세'}
            </Text>
          </VStack>
        </>
      ) : (
        <>
          <Image
            src={imagePreview}
            fit="cover"
            w={'100%'}
            h={'100%'}
            onClick={() => props.onPreviewImage(imagePreview)}
          />
          <Flex
            boxSize={'2.25rem'}
            position={'absolute'}
            top={'-1rem'}
            right={'-0.8rem'}
            justifyContent="center"
            alignItems={'center'}
            zIndex={100}
            onClick={onDeleteImage}>
            <Image src={'/assets/images/icn_close-circle.svg'} boxSize="100%" />
          </Flex>
        </>
      )}
    </Box>
  );
}

export default CaptureButton;
